import "./SupplierDetailPage.css";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type SupplierData = {
  id: string;
  organization_id: string;
  supplier_type: "person" | "company";
  document_type: string | null;
  document_number: string | null;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  address: string | null;
  city: string | null;
  country_code: string;
  notes: string | null;
  status: "active" | "inactive" | "archived";
};

type PurchaseOrderRow = {
  id: string;
  purchase_number: string;
  purchase_scope: string;
  status: string;
  order_date: string;
  expected_date: string | null;
  currency_code: string;
  total: number | string;
  updated_at: string;
};

type ImportShipmentRow = {
  id: string;
  purchase_order_id: string;
  import_number: string;
  status: string;
  origin_country_code: string | null;
  destination_country_code: string | null;
  incoterm: string | null;
  supplier_currency_code: string | null;
  shipment_date: string | null;
  estimated_arrival_date: string | null;
  arrival_date: string | null;
  updated_at: string;
};

type AccountingDocumentRow = {
  id: string;
  purchase_order_id: string | null;
  document_type: string;
  series: string;
  document_number: string;
  issue_date: string;
  due_date: string | null;
  currency_code: string;
  total: number | string;
  status: string;
  sunat_status: string;
  updated_at: string;
};

type PayableRow = {
  id: string;
  purchase_document_id: string;
  currency_code: string;
  original_amount: number | string;
  paid_amount: number | string;
  balance_amount: number | string;
  issue_date: string;
  due_date: string | null;
  status: string;
  updated_at: string;
};

type SupplierSummary = {
  purchase_order_count: number | string;
  import_shipment_count: number | string;
  accounting_document_count: number | string;
  open_payable_count: number | string;
  open_payable_pen: number | string;
  open_payable_usd: number | string;
};

type SupplierBusinessDetail = {
  supplier: SupplierData;
  purchase_orders: PurchaseOrderRow[];
  import_shipments: ImportShipmentRow[];
  accounting_documents: AccountingDocumentRow[];
  payables: PayableRow[];
  summary: SupplierSummary;
};

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function date(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-PE", {
    dateStyle: "medium",
  }).format(new Date(`${value}T00:00:00`));
}

export function SupplierDetailPage() {
  const { supplierId = "" } = useParams();
  const { selectedOrganization } = useOrganization();

  const [detail, setDetail] = useState<SupplierBusinessDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supplierId) {
      setLoadError("Proveedor inválido.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const { data, error } = await supabase.rpc(
      "get_supplier_business_detail",
      { p_supplier_id: supplierId },
    );

    if (error) {
      setDetail(null);
      setLoadError(error.message);
      setLoading(false);
      return;
    }

    const next = data as SupplierBusinessDetail;

    if (
      selectedOrganization &&
      next.supplier.organization_id !== selectedOrganization.organizationId
    ) {
      setDetail(null);
      setLoadError("El proveedor no pertenece a la empresa seleccionada.");
      setLoading(false);
      return;
    }

    setDetail(next);
    setLoading(false);
  }, [supplierId, selectedOrganization]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Cargando proveedor…</strong>
        </section>
      </div>
    );
  }

  if (loadError || !detail) {
    return (
      <div className="page">
        <Link className="compact-button supplier-detail-back" to="/maestros">
          ← Volver a Maestros
        </Link>
        <section className="empty-state empty-state-error">
          <strong>No se pudo abrir el proveedor.</strong>
          <p>{loadError ?? "Proveedor no disponible."}</p>
        </section>
      </div>
    );
  }

  const {
    supplier,
    purchase_orders,
    import_shipments,
    accounting_documents,
    payables,
    summary,
  } = detail;

  return (
    <div className="page">
      <div className="supplier-detail-topline">
        <Link className="compact-button" to="/maestros">
          ← Volver a Maestros
        </Link>
        <span
          className={
            supplier.status === "active"
              ? "status-badge status-badge-approved"
              : "status-badge"
          }
        >
          {supplier.status}
        </span>
      </div>

      <header className="page-header">
        <div>
          <span className="eyebrow">
            {supplier.supplier_type === "company" ? "Empresa" : "Persona"}
          </span>
          <h1>{supplier.name}</h1>
          <p>
            {supplier.document_number
              ? `${supplier.document_type ?? "Doc."} ${supplier.document_number}`
              : "Sin documento registrado"}
          </p>
        </div>
      </header>

      <section className="supplier-summary-grid">
        <div className="metric-card">
          <span>Órdenes de compra</span>
          <strong>{Number(summary.purchase_order_count)}</strong>
        </div>
        <div className="metric-card">
          <span>Importaciones</span>
          <strong>{Number(summary.import_shipment_count)}</strong>
        </div>
        <div className="metric-card">
          <span>Comprobantes</span>
          <strong>{Number(summary.accounting_document_count)}</strong>
        </div>
        <div className="metric-card">
          <span>CxP abiertas</span>
          <strong>{Number(summary.open_payable_count)}</strong>
        </div>
      </section>

      <section className="supplier-detail-grid">
        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Contacto</span>
              <h2>Datos del proveedor</h2>
            </div>
          </div>

          <dl className="supplier-definition">
            <div>
              <dt>Contacto</dt>
              <dd>{supplier.contact_name ?? "—"}</dd>
            </div>
            <div>
              <dt>País</dt>
              <dd>{supplier.country_code || "—"}</dd>
            </div>
            <div>
              <dt>Correo</dt>
              <dd>{supplier.email ?? "—"}</dd>
            </div>
            <div>
              <dt>Teléfono</dt>
              <dd>{supplier.phone ?? "—"}</dd>
            </div>
            <div>
              <dt>WhatsApp</dt>
              <dd>{supplier.whatsapp ?? "—"}</dd>
            </div>
            <div>
              <dt>Ciudad</dt>
              <dd>{supplier.city ?? "—"}</dd>
            </div>
            <div className="supplier-span">
              <dt>Dirección</dt>
              <dd>{supplier.address ?? "—"}</dd>
            </div>
            <div className="supplier-span">
              <dt>Notas</dt>
              <dd>{supplier.notes ?? "—"}</dd>
            </div>
          </dl>
        </article>

        <article className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Finanzas</span>
              <h2>Saldo pendiente</h2>
            </div>
            <Link className="compact-button" to="/tesoreria">
              Abrir Tesorería
            </Link>
          </div>

          <div className="supplier-balance-grid">
            <div>
              <span>PEN</span>
              <strong>{money(summary.open_payable_pen, "PEN")}</strong>
            </div>
            <div>
              <span>USD</span>
              <strong>{money(summary.open_payable_usd, "USD")}</strong>
            </div>
          </div>

          <div className="supplier-compact-list">
            {payables.length === 0 ? (
              <p className="settings-empty">Sin cuentas por pagar pendientes.</p>
            ) : (
              payables.map((payable) => (
                <div className="supplier-compact-row" key={payable.id}>
                  <div>
                    <strong>{money(payable.balance_amount, payable.currency_code)}</strong>
                    <span>
                      Emisión {date(payable.issue_date)}
                      {payable.due_date ? ` · Vence ${date(payable.due_date)}` : ""}
                    </span>
                  </div>
                  <span className="status-badge">{payable.status}</span>
                </div>
              ))
            )}
          </div>
        </article>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Compras</span>
            <h2>Órdenes de compra</h2>
          </div>
          <Link className="compact-button" to="/compras">
            Ver módulo
          </Link>
        </div>

        <div className="supplier-compact-list">
          {purchase_orders.length === 0 ? (
            <p className="settings-empty">Sin órdenes de compra registradas.</p>
          ) : (
            purchase_orders.map((order) => (
              <Link
                className="supplier-compact-row supplier-compact-link"
                key={order.id}
                to={`/compras/${order.id}`}
              >
                <div>
                  <strong>{order.purchase_number}</strong>
                  <span>
                    {date(order.order_date)} · {money(order.total, order.currency_code)}
                    {order.purchase_scope === "import" ? " · Importación" : ""}
                  </span>
                </div>
                <span className="status-badge">{order.status}</span>
              </Link>
            ))
          )}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Logística</span>
            <h2>Importaciones</h2>
          </div>
          <Link className="compact-button" to="/importaciones">
            Ver módulo
          </Link>
        </div>

        <div className="supplier-compact-list">
          {import_shipments.length === 0 ? (
            <p className="settings-empty">Sin importaciones registradas.</p>
          ) : (
            import_shipments.map((shipment) => (
              <Link
                className="supplier-compact-row supplier-compact-link"
                key={shipment.id}
                to={`/importaciones/${shipment.id}`}
              >
                <div>
                  <strong>{shipment.import_number}</strong>
                  <span>
                    {[shipment.origin_country_code, shipment.incoterm]
                      .filter(Boolean)
                      .join(" · ") || "Sin datos logísticos"}
                    {shipment.arrival_date
                      ? ` · Llegada ${date(shipment.arrival_date)}`
                      : shipment.estimated_arrival_date
                        ? ` · ETA ${date(shipment.estimated_arrival_date)}`
                        : ""}
                  </span>
                </div>
                <span className="status-badge">{shipment.status}</span>
              </Link>
            ))
          )}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Contabilidad</span>
            <h2>Comprobantes recibidos</h2>
          </div>
          <Link className="compact-button" to="/contabilidad/compras-recibidas">
            Ver comprobantes
          </Link>
        </div>

        <div className="supplier-compact-list">
          {accounting_documents.length === 0 ? (
            <p className="settings-empty">Sin comprobantes registrados.</p>
          ) : (
            accounting_documents.map((document) => (
              <div className="supplier-compact-row" key={document.id}>
                <div>
                  <strong>
                    {document.series}-{document.document_number}
                  </strong>
                  <span>
                    {document.document_type} · {date(document.issue_date)} ·{" "}
                    {money(document.total, document.currency_code)}
                  </span>
                </div>
                <div className="supplier-document-status">
                  <span className="status-badge">{document.status}</span>
                  <small>Fiscal {document.sunat_status}</small>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
